import React, { createContext, useState, useContext, useEffect } from 'react';

import axios from 'axios';

import { BASE_URL } from './helpers.js';
import { useAuth } from "./AuthContext";

const AppStateContext = createContext();

export const useAppState = () => {
    return useContext(AppStateContext);
};

export const AppStateProvider = ({ children }) => {
    const { clearAuth } = useAuth();

    const [classes, setClasses] = useState([]);
    const [error, setError] = useState(null);
    const [isLoadingClasses, setIsLoadingClasses] = useState(true);

    // Axios resolves only for 2xx responses and rejects everything else, so
    // any response returned from these helpers is a success.
    const callApi = async (method, endpoint, data) => {
        const url = `${BASE_URL}/api/${endpoint}`;
        let result;
        try {
            if (method === 'get') {
                result = await axios.get(url);
            } else if (method === 'post') {
                result = await axios.post(url, data);
            } else if (method === 'put') {
                result = await axios.put(url, data);
            } else if (method === 'delete') {
                result = await axios.delete(url);
            }
        } catch (err) {
            if (err?.response?.status === 401) {
                // The token is no longer valid. Clearing it logs the user out
                // locally, and the RequireAuth guard redirects to the login
                // page. Rethrow so callers never see an undefined response.
                clearAuth();
            }
            throw err;
        }
        return result;
    };

    const apiGet = (endpoint) => {
        return callApi('get', endpoint);
    };

    const apiPost = (endpoint, data) => {
        return callApi('post', endpoint, data);
    };

    const apiPut = (endpoint, data) => {
        return callApi('put', endpoint, data);
    };

    const apiDelete = (endpoint) => {
        return callApi('delete', endpoint);
    };

    const fetchClasses = async () => {
        setError(null);
        try {
            const response = await apiGet('classes');
            setClasses(response.data);
        } catch (err) {
            setError('An error occurred while trying to load the classes.');
        } finally {
            setIsLoadingClasses(false);
        }
    };

    useEffect(() => {
        fetchClasses();
    }, []);

    const value = {
        apiGet, apiPost, apiDelete, apiPut,
        classes,
        fetchClasses,
        isLoadingClasses,
        error,
    };

    return (
        <AppStateContext.Provider value={value}>
            {children}
        </AppStateContext.Provider>
    );
};
